import React, { useState, useEffect, useRef } from 'react';
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTools, faChartBar, faChartLine, faHome, faUserCircle, faSearch, faAngleDown, faNewspaper, faBell } from '@fortawesome/free-solid-svg-icons';
import AsyncSelect from 'react-select/async';
import { components } from 'react-select';
import { debounce } from 'lodash';
import axios from 'axios';
import SignInModal from './SignInModal';
import { handleTokenAndPostRequest, trackEvent } from '../utils/utils';
import { computeBranding, getEffectiveDomain } from '../utils/useAgentDetails';


const MainNav = ({ onLoaded }) => {

  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Extract the last segment of the path
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const lastSegment = pathSegments[pathSegments.length - 1];

  // Only use path param if it's one of the allowed slugs
  const allowedSlugs = ['stylux-properties', 'spica-real-estate'];
  const customDomain = getEffectiveDomain();
  const explicitSlug = allowedSlugs.includes(lastSegment)
    ? lastSegment
    : searchParams.get('agent');
  const agent_slug = explicitSlug || (customDomain ? null : 'home-price');
  const cacheKey = agent_slug || customDomain;

  console.log('main nav agent_slug:', agent_slug, 'customDomain:', customDomain);

  const [agentDetails, setAgentDetails] = useState(() => {
    const storedAgentDetails = sessionStorage.getItem('agentDetails');
    const storedKey = sessionStorage.getItem('landing_page_slug');
    if (storedAgentDetails && storedKey === cacheKey) {
      return JSON.parse(storedAgentDetails);
    }
    return null;
  });

  const [isLoading, setIsLoading] = useState(true);

  // Synchronously clear stale branding when agent changes (before paint)
  const prevKeyRef = useRef(cacheKey);
  if (prevKeyRef.current !== cacheKey) {
    prevKeyRef.current = cacheKey;
    if (agentDetails) setAgentDetails(null);
    if (!isLoading) setIsLoading(true);
  }

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1200);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectKey, setSelectKey] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    const storedKey = sessionStorage.getItem('landing_page_slug');
    const storedAgentDetails = JSON.parse(sessionStorage.getItem('agentDetails') || 'null');

    if (storedAgentDetails && storedKey === cacheKey) {
      setAgentDetails(storedAgentDetails);
      setIsLoading(false);
      if (onLoaded) onLoaded();
    } else {
      setAgentDetails(null);   // clear stale branding immediately
      setIsLoading(true);
      const fetchAgentDetails = async () => {
        const url = `${window.api_host}/agency`;
        const payload = agent_slug
          ? { agent_slug }
          : { custom_domain: customDomain };
        try {
          const response = await handleTokenAndPostRequest(
            url,
            payload,
            { 'Content-Type': 'application/json' }
          );
          const data = response.data;
          setAgentDetails(data);
          console.log('setting agentDetails:', data);
          sessionStorage.setItem('agentDetails', JSON.stringify(data));
          sessionStorage.setItem('landing_page_slug', cacheKey);
        } catch (error) {
          console.error('Error:', error);
        } finally {
          setIsLoading(false);
          if (onLoaded) onLoaded();
        }
      };
      fetchAgentDetails();
    }
  }, [cacheKey, onLoaded]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1200);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const jwtToken = localStorage.getItem('jwt_token');
    if (jwtToken) {
      handleTokenAndPostRequest(`${window.api_host}/authenticate-token`, {})
        .then(response => {
          setUserEmail(response.data.email);
        })
        .catch(error => {
          console.error('Error authenticating token:', error);
          localStorage.removeItem('jwt_token');
        });
    }
  }, []);

  useEffect(() => {
    setSelectedProperty(null);
    setSelectKey(prevKey => prevKey + 1);
  }, [location]);

  const getLinkStyle = (path) => ({
    textAlign: 'center',
    color: agentDetails.agent_button_colour,
    fontSize: isMobile ? '0.75rem' : '0.82rem',
    fontWeight: location.pathname === path ? 600 : 500,
    opacity: location.pathname === path ? 1 : 0.85,
    whiteSpace: 'nowrap',
    letterSpacing: '0.01em',
  });

  const getIconStyle = (path) => ({
    color: agentDetails.agent_button_colour,
    opacity: location.pathname === path ? 1 : 0.7,
  });

  const loadProperties = debounce((inputValue, callback) => {
    if (!inputValue || inputValue.length <= 1) {
      callback([]);
      return;
    }
    axios.get(`${window.api_host}/property-search?q=${inputValue}`)
      .then(response => {
        const options = response.data.map(property => ({
          label: property.address_text,
          value: property.id,
          slug: property.slug
        }));
        callback(options);
      })
      .catch(error => {
        console.error('Error loading property options:', error);
        callback([]);
      });
  }, 500);

  const handlePropertyChange = (selectedOption) => {
    setSelectedProperty(selectedOption);
    if (selectedOption) {
      const property_id = selectedOption.value;
      const slug = selectedOption.slug;
      const agentSlug = agentDetails.landing_page_slug;
      console.log('Property selected:', property_id, slug, agentSlug);
      navigate(
        `/property-profile/${slug}-${property_id}` +
        (agentSlug === "home-price" ? "" : `?agent=${encodeURIComponent(agentSlug)}`)
      );
    }
  };

  const DropdownIndicator = (props) => {
    return (
      <components.DropdownIndicator {...props}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FontAwesomeIcon icon={faSearch} style={{ padding: '0px', marginRight: '8px', marginTop: '0px', marginLeft: '0px' }} />
        </div>
      </components.DropdownIndicator>
    );
  };

  const handleModalSuccess = (email) => {
    console.log("Modal success", email);
    setUserEmail(email);
  };

  const isLight = computeBranding(agentDetails)?.isLight;

  const Control = ({ children, ...props }) => (
    <components.Control {...props}>
      <FontAwesomeIcon icon={faSearch} size="sm" style={{color: isLight ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.5)', marginLeft: '10px'}}/> {children}
    </components.Control>
  );

  const handleLogout = () => {
    localStorage.removeItem('jwt_token');
    setUserEmail(null);
    const agentParam = agentDetails.landing_page_slug !== 'home-price' ? `?agent=${agentDetails.landing_page_slug}` : '';
    window.location.href = `/${agentParam}`;
  };

  // Guard: Don't render until agentDetails is loaded
  if (!agentDetails) return null;

  return (
    <>
      {!isMobile ? (
        <Navbar expand="lg" className="main-navbar" style={{ backgroundColor: agentDetails.agent_colour, position: 'fixed', top: 0, width: '100%', zIndex: 9998, borderBottom: 'none', boxShadow: '0 1px 8px rgba(0,0,0,0.08)', margin: 0, padding: '0' }}>
          <Container fluid style={{ maxWidth: 1280, padding: '0 24px' }}>
          <Navbar.Brand as={Link} to={agentDetails.landing_page_slug === "home-price" ? "/" : `/?agent=${agentDetails.landing_page_slug}`} style={{ padding: '10px 0', marginRight: 16 }}>
              <img
                src={agentDetails.logo_url}
                className="d-inline-block align-top"
                alt="Agent logo"
                style={{ maxHeight: 36, maxWidth: 140, objectFit: 'contain' }}
              />
            </Navbar.Brand>
            <Nav className="me-auto" style={{ gap: 2, alignItems: 'center' }}>
            <Nav.Link
              as={Link}
              to={agentDetails.landing_page_slug === "home-price" ? "/property-search-live" : `/property-search-live?agent=${agentDetails.landing_page_slug}`}
              style={getLinkStyle("/property-search-live")}
              onClick={() => trackEvent('click_nav', { tab: 'valuation' })}
            >
              Price Estimate
            </Nav.Link>

            <Nav.Link
              as={Link}
              to={agentDetails.landing_page_slug === "home-price" ? "/suburb-search" : `/suburb-search?agent=${agentDetails.landing_page_slug}`}
              style={getLinkStyle("/suburb-search")}
              onClick={() => trackEvent('click_nav', { tab: 'suburbs' })}
            >
              Suburbs
            </Nav.Link>

            <Nav.Link
              as={Link}
              to={agentDetails.landing_page_slug === "home-price" ? "/market-activity" : `/market-activity?agent=${agentDetails.landing_page_slug}`}
              style={getLinkStyle("/market-activity")}
              onClick={() => trackEvent('click_nav', { tab: 'comparables' })}
            >
              Comparables
            </Nav.Link>

            <Nav.Link
              as={Link}
              to={agentDetails.landing_page_slug === "home-price" ? "/notifications" : `/notifications?agent=${agentDetails.landing_page_slug}`}
              style={getLinkStyle("/notifications")}
              onClick={() => trackEvent('click_nav', { tab: 'notifications' })}
            >
              Notifications
            </Nav.Link>

            <Nav.Link
              as={Link}
              to={agentDetails.landing_page_slug === "home-price" ? "/news" : `/news?agent=${agentDetails.landing_page_slug}`}
              style={getLinkStyle("/news")}
              onClick={() => trackEvent('click_nav', { tab: 'news' })}
            >
              News
            </Nav.Link>
            </Nav>
            <Nav className="ms-auto" style={{ alignItems: 'center', gap: 8 }}>
              <AsyncSelect
                key={selectKey}
                cacheOptions
                defaultOptions
                loadOptions={loadProperties}
                onChange={handlePropertyChange}
                value={selectedProperty}
                noOptionsMessage={() => null}
                placeholder="Search property address…"
                openMenuOnClick={false}
                components={{ Control, DropdownIndicator:() => null, IndicatorSeparator:() => null }}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    borderRadius: 10,
                    textAlign: 'left',
                    width: 'clamp(240px, 28vw, 400px)',
                    boxShadow: 'none',
                    borderColor: isLight ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.18)',
                    backgroundColor: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.12)',
                    '&:hover': {
                      borderColor: isLight ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.35)',
                      backgroundColor: isLight ? 'rgba(0,0,0,0.10)' : 'rgba(255,255,255,0.18)',
                    },
                    fontSize: '0.78rem',
                    paddingLeft: '0px',
                    minHeight: 36,
                    height: 36,
                    transition: 'all 0.2s ease',
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    padding: '0 8px',
                  }),
                  input: (provided) => ({
                    ...provided,
                    color: isLight ? '#1a1a1a' : '#fff',
                    margin: 0,
                    padding: 0,
                  }),
                  placeholder: (provided) => ({
                    ...provided,
                    textAlign: 'left',
                    fontSize: '0.78rem',
                    color: isLight ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.55)',
                  }),
                  menu: (provided) => ({
                    ...provided,
                    textAlign: 'left',
                    fontSize: '0.82rem',
                    maxWidth: '100vw',
                    borderRadius: 12,
                    overflow: 'hidden',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isFocused ? '#f3f4f6' : '#fff',
                    color: '#1a1a1a',
                    fontSize: '0.82rem',
                  }),
                  singleValue: (provided) => ({
                    ...provided,
                    textAlign: 'left',
                    color: isLight ? '#1a1a1a' : '#fff',
                  })
                }}
              />
              {userEmail ? (
                <Dropdown align="end">
                  <Dropdown.Toggle variant="link" id="dropdown-basic" style={{ color: agentDetails.agent_button_colour, textDecoration: 'none', padding: '4px 8px' }}>
                    <FontAwesomeIcon icon={faUserCircle} size="lg" />
                  </Dropdown.Toggle>

                  <Dropdown.Menu style={{ '--dropdown-active-bg': `${agentDetails.agent_colour}1a`, '--dropdown-active-color': agentDetails.agent_colour, borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.10)', border: '1px solid #f3f4f6', padding: '4px 0', overflow: 'hidden', minWidth: 180 }}>
                  <Dropdown.Item as={Link} to={agentDetails.landing_page_slug === "home-price" ? "/notifications" : `/notifications?agent=${agentDetails.landing_page_slug}`} style={{ color: '#212529', fontSize: '0.85rem', padding: '8px 16px' }}>Notifications</Dropdown.Item>
                    <Dropdown.Divider style={{ margin: '4px 0' }} />
                    <Dropdown.Item onClick={handleLogout} style={{ color: '#212529', fontSize: '0.85rem', padding: '8px 16px' }}>Logout</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              ) : (
                <Button
                  variant="primary"
                  style={{ 
                    backgroundColor: agentDetails.agent_button_colour, 
                    border: `1px solid ${agentDetails.agent_button_colour}`,
                    color: isLight ? agentDetails.agent_font_colour || '#fff' : agentDetails.agent_colour,
                    borderRadius: 8,
                    fontWeight: 600,
                    fontSize: '0.78rem',
                    padding: '7px 16px',
                    whiteSpace: 'nowrap',
                    letterSpacing: '0.01em',
                  }}
                  onClick={() => { trackEvent('click_sign_in_nav'); setShowSignInModal(true); }}
                >
                  Sign In
                </Button>
              )}
            </Nav>
          </Container>
        </Navbar>
      ) : (
        <>
          <div style={{ 
            top: 0, 
            width: '100%', 
            backgroundColor: agentDetails.agent_colour, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            padding: '10px', 
            zIndex: 9998,
            position: 'fixed'
          }}>
            <img
              src={agentDetails.logo_url}
              className="d-inline-block align-top"
              alt="Agent logo"
              style={{ maxHeight: 32, maxWidth: 120, objectFit: 'contain', marginRight: '8px' }}
            />
            <div style={{ flex: 1, margin: '0 10px 0px 0px' }}>
              <AsyncSelect
                key={selectKey}
                cacheOptions
                defaultOptions
                loadOptions={loadProperties}
                onChange={handlePropertyChange}
                value={selectedProperty}
                noOptionsMessage={() => null}
                placeholder="Search address…"
                components={{ Control, DropdownIndicator:() => null, IndicatorSeparator:() => null }}
                openMenuOnClick={false}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    borderRadius: 10,
                    textAlign: 'left',
                    width: '100%',
                    boxShadow: 'none',
                    borderColor: isLight ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.25)',
                    backgroundColor: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.12)',
                    '&:hover': {
                      borderColor: isLight ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.4)',
                      backgroundColor: isLight ? 'rgba(0,0,0,0.10)' : 'rgba(255,255,255,0.18)',
                    },
                    fontSize: '0.78rem',
                    paddingLeft: '0px',
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    padding: '0 8px',
                  }),
                  input: (provided) => ({
                    ...provided,
                    color: isLight ? '#1a1a1a' : '#fff',
                    margin: 0,
                    padding: 0,
                  }),
                  placeholder: (provided) => ({
                    ...provided,
                    textAlign: 'left',
                    fontSize: '0.78rem',
                    color: isLight ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.55)',
                  }),
                  menu: (provided) => ({
                    ...provided,
                    textAlign: 'left',
                    fontSize: '0.82rem',
                    maxWidth: '100vw',
                    borderRadius: 12,
                    overflow: 'hidden',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isFocused ? '#f3f4f6' : '#fff',
                    color: '#1a1a1a',
                    fontSize: '0.82rem',
                  }),
                  singleValue: (provided) => ({
                    ...provided,
                    textAlign: 'left',
                    color: isLight ? '#1a1a1a' : '#fff',
                  })
                }}
              />
            </div>
            {userEmail ? (
              <Dropdown align="end">
                <Dropdown.Toggle variant="link" id="dropdown-basic" style={{ color: agentDetails.agent_button_colour, textDecoration: 'none' }}>
                  <FontAwesomeIcon icon={faUserCircle} size="2x" />
                </Dropdown.Toggle>

                <Dropdown.Menu style={{ '--dropdown-active-bg': `${agentDetails.agent_colour}1a`, '--dropdown-active-color': agentDetails.agent_colour, borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.10)', border: '1px solid #f3f4f6', padding: '4px 0', overflow: 'hidden' }}>
                <Dropdown.Item as={Link} to={agentDetails.landing_page_slug === "home-price" ? "/notifications" : `/notifications?agent=${agentDetails.landing_page_slug}`} style={{ color: '#212529', fontSize: '0.88rem', padding: '8px 16px' }}>Notifications</Dropdown.Item>
                  <Dropdown.Divider style={{ margin: '4px 0' }} />
                  <Dropdown.Item onClick={handleLogout} style={{ color: '#212529', fontSize: '0.88rem', padding: '8px 16px' }}>Logout</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            ) : (
              <FontAwesomeIcon 
                icon={faUserCircle} 
                size="2x" 
                style={{ color: agentDetails.agent_button_colour, cursor: 'pointer', marginTop: '2px' }}
                onClick={() => { trackEvent('click_sign_in_nav'); setShowSignInModal(true); }}
              />
            )}
          </div>
          <div style={{ 
            bottom: 0, 
            position: 'fixed',
            width: '100%', 
            backgroundColor: agentDetails.agent_colour, 
            display: 'flex', 
            justifyContent: 'space-around', 
            padding: '10px 0', 
            zIndex: 9998
          }}>
            <Nav.Link as={Link} to={agentDetails.landing_page_slug === "home-price" ? "/property-search-live" : `/property-search-live?agent=${agentDetails.landing_page_slug}`} style={getLinkStyle("/property-search-live")} onClick={() => trackEvent('click_nav', { tab: 'valuation' })}>
              <FontAwesomeIcon icon={faTools} size="lg" style={getIconStyle("/property-search-live")} />
              <div>Estimate</div>
            </Nav.Link>

            <Nav.Link as={Link} to={agentDetails.landing_page_slug === "home-price" ? "/suburb-search" : `/suburb-search?agent=${agentDetails.landing_page_slug}`} style={getLinkStyle("/suburb-search")} onClick={() => trackEvent('click_nav', { tab: 'suburbs' })}>
              <FontAwesomeIcon icon={faChartBar} size="lg" style={getIconStyle("/suburb-search")} />
              <div>Suburbs</div>
            </Nav.Link>

            <Nav.Link as={Link} to={agentDetails.landing_page_slug === "home-price" ? "/market-activity" : `/market-activity?agent=${agentDetails.landing_page_slug}`} style={getLinkStyle("/market-activity")} onClick={() => trackEvent('click_nav', { tab: 'comparables' })}>
              <FontAwesomeIcon icon={faChartLine} size="lg" style={getIconStyle("/market-activity")} />
              <div>Comparables</div>
            </Nav.Link>

            <Nav.Link as={Link} to={agentDetails.landing_page_slug === "home-price" ? "/notifications" : `/notifications?agent=${agentDetails.landing_page_slug}`} style={getLinkStyle("/notifications")} onClick={() => trackEvent('click_nav', { tab: 'notifications' })}>
              <FontAwesomeIcon icon={faBell} size="lg" style={getIconStyle("/notifications")} />
              <div>Notifications</div>
            </Nav.Link>

            <Nav.Link as={Link} to={agentDetails.landing_page_slug === "home-price" ? "/news" : `/news?agent=${agentDetails.landing_page_slug}`} style={getLinkStyle("/news")} onClick={() => trackEvent('click_nav', { tab: 'news' })}>
              <FontAwesomeIcon icon={faNewspaper} size="lg" style={getIconStyle("/news")} />
              <div>News</div>
            </Nav.Link>
          </div>
        </>
      )}
      <SignInModal
        show={showSignInModal}
        onHide={() => setShowSignInModal(false)}
        agentDetails={agentDetails}
        onSuccess={handleModalSuccess}
      />
    </>
  );
};

export default MainNav;